/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { apiSlice } from './apiSlice';
import Vehicle from '../../core/models/Vehicle';
import Auction from '../../core/models/Auction';
import AuctionVehicles from '../../core/models/AuctionVehicles';
import AuctionItem from '../../core/models/AuctionItem';
import LiveAuctionStatus from '../../core/models/LiveAuctionStatus';
import InitialAuctionStatus from '../../core/models/InitialAuctionStatus';

export const auctionApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getActiveAuction: builder.query<Auction, void>({
      query: () => '/auctions/active',
      providesTags: (result, error, id) => [{ type: 'Auction' }],
    }),
    getPastAuctions: builder.query<Auction[], void>({
      query: () => '/auctions/closed',
      keepUnusedDataFor: 5,
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Auction' as const, id })),
              { type: 'Auction', id: 'LIST' },
            ]
          : [{ type: 'Auction', id: 'LIST' }],
    }),
    getAuctionById: builder.query<Auction, string>({
      query: (auctionId) => `/auctions/${auctionId}`,
      providesTags: (result, error, id) => [{ type: 'Auction' }],
    }),
    // getAuctionById: builder.mutation<Auction, string>({
    //     query: (auctionId) => `/auctions/${auctionId}`,
    // }),
    getAuctionItemById: builder.query<AuctionItem, string>({
      query: (auctionItemId) => `/auctions/auctionItem/${auctionItemId}`,
      providesTags: (result, error, id) => [{ type: 'AuctionItem' }],
    }),
    getLiveAuctionStatus: builder.query<InitialAuctionStatus, string>({
      query: (auctionId) => `/auctions/inProgressAuctionStatus/${auctionId}`,
    }),
    updateAuction: builder.mutation<Auction, Date>({
      query: (auctionDate) => ({
        url: '/auctions',
        method: 'PUT',
        body: { auctionDate: auctionDate },
      }),
      invalidatesTags: ['Auction'],
    }),
    cancelAuction: builder.mutation<void, string>({
      query: (auctionId) => ({
        url: '/auctions/cancel',
        method: 'PUT',
        body: { auctionId: auctionId },
      }),
      invalidatesTags: ['Auction'],
    }),
    getEligibleVehicles: builder.query<Vehicle[], void>({
      query: () => '/auctions/eligibleVehicles',
      providesTags: (result, error, id) => [{ type: 'EligibleVehicles' }],
    }),
    updateAuctionVehicles: builder.mutation<Auction, AuctionVehicles>({
      query: (auctionVehicles) => ({
        url: '/auctions/vehicles',
        method: 'PUT',
        body: auctionVehicles,
      }),
      invalidatesTags: ['Auction'],
    }),
    startAuction: builder.mutation<LiveAuctionStatus, string>({
      query: (auctionId) => ({
        url: '/auctions/start',
        method: 'PUT',
        body: { auctionId: auctionId },
      }),
      invalidatesTags: ['Auction'],
    }),
    pauseAuction: builder.mutation<LiveAuctionStatus, string>({
      query: (auctionId) => ({
        url: '/auctions/pause',
        method: 'PUT',
        body: { auctionId: auctionId },
      }),
      invalidatesTags: ['Auction'],
    }),
    resumeAuction: builder.mutation<LiveAuctionStatus, string>({
      query: (auctionId) => ({
        url: '/auctions/resume',
        method: 'PUT',
        body: { auctionId: auctionId },
      }),
      invalidatesTags: ['Auction'],
    }),
    goToNextAuctionItem: builder.mutation<LiveAuctionStatus, string>({
      query: (auctionId) => ({
        url: '/auctions/moveToNextItem',
        method: 'PUT',
        body: { auctionId: auctionId },
      }),
      invalidatesTags: ['Auction'],
    }),
    stopAuction: builder.mutation<Auction, string>({
      query: (auctionId) => ({
        url: '/auctions/stop',
        method: 'PUT',
        body: { auctionId: auctionId },
      }),
      invalidatesTags: ['Auction'],
    }),
    getNextAuctionUsers: builder.query<any[], void>({
      query: () => '/auctions/buyers/registered',
      providesTags: (result, error, id) => [{ type: 'RegisteredUsers' }],
    }),
  }),
});

export const {
  useGetActiveAuctionQuery,
  useGetPastAuctionsQuery,
  useGetAuctionItemByIdQuery,
  useGetAuctionByIdQuery,
  useGetLiveAuctionStatusQuery,
  useGetEligibleVehiclesQuery,
  useUpdateAuctionMutation,
  useCancelAuctionMutation,
  useUpdateAuctionVehiclesMutation,
  useStartAuctionMutation,
  usePauseAuctionMutation,
  useResumeAuctionMutation,
  useGoToNextAuctionItemMutation,
  useStopAuctionMutation,
  useGetNextAuctionUsersQuery,
} = auctionApiSlice;
